import React from 'react';
import { Maximize2Icon, Minimize2Icon, XIcon, LayoutIcon, ExternalLinkIcon, SettingsIcon } from './Icons';
import ScaleIcon from '../../../assets/image/scale-icon.svg';

interface HeaderProps {
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onClose?: () => void;
  commandCenterMode?: boolean;
  onToggleCommandCenter?: () => void;
  showCommandCenterToggle?: boolean;
  onExpandToFullPage?: () => void;
  settingsMode?: boolean;
  onToggleSettings?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isMaximized = false,
  onToggleMaximize,
  onClose,
  commandCenterMode = false,
  onToggleCommandCenter,
  showCommandCenterToggle = false,
  onExpandToFullPage,
  settingsMode = false,
  onToggleSettings,
}) => {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'linear-gradient(135deg, #1F313A 0%, #2B3B44 100%)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    gap: '12px',
    minHeight: '52px',
  };

  const logoStyle: React.CSSProperties = {
    width: '28px',
    height: '28px',
    flexShrink: 0,
  };

  const titleContainerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 700,
    background: 'linear-gradient(90deg, #33AA40, #4DC95A)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1.2,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '11px',
    color: commandCenterMode ? '#4DC95A' : settingsMode ? '#64B5F6' : '#8899A6',
    marginTop: '1px',
  };

  const ccToggleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: commandCenterMode ? '1px solid rgba(51,170,64,0.4)' : 'none',
    background: commandCenterMode ? 'rgba(51,170,64,0.2)' : 'rgba(255,255,255,0.08)',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.15s ease',
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const iconButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: 'none',
    background: 'rgba(255,255,255,0.08)',
    cursor: 'pointer',
    padding: 0,
    transition: 'background-color 0.15s ease',
  };

  return (
    <div style={containerStyle}>
      <ScaleIcon style={logoStyle} aria-label="Equa" />
      <div style={titleContainerStyle}>
        <span style={titleStyle}>Equanaut</span>
        <span style={subtitleStyle}>
          {settingsMode ? 'Settings' : commandCenterMode ? 'Command Center Mode' : 'AI Assistant'}
        </span>
      </div>
      <div style={buttonGroupStyle}>
        {showCommandCenterToggle && onToggleCommandCenter && (
          <button
            style={ccToggleStyle}
            onClick={onToggleCommandCenter}
            title={commandCenterMode ? 'Exit Command Center Mode' : 'Command Center Mode'}
            onMouseEnter={(e) => {
              if (!commandCenterMode) {
                (e.currentTarget as HTMLElement).style.background = 'rgba(51,170,64,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!commandCenterMode) {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
              }
            }}
          >
            <LayoutIcon size={14} color={commandCenterMode ? '#4DC95A' : '#C8D6DF'} />
          </button>
        )}
        {onToggleSettings && (
          <button
            style={{
              ...iconButtonStyle,
              border: settingsMode ? '1px solid rgba(100,181,246,0.4)' : 'none',
              background: settingsMode ? 'rgba(100,181,246,0.2)' : 'rgba(255,255,255,0.08)',
            }}
            onClick={onToggleSettings}
            title={settingsMode ? 'Close Settings' : 'Equabot Settings'}
            onMouseEnter={(e) => {
              if (!settingsMode) {
                (e.currentTarget as HTMLElement).style.background = 'rgba(100,181,246,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!settingsMode) {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
              }
            }}
          >
            <SettingsIcon size={14} color={settingsMode ? '#64B5F6' : '#C8D6DF'} />
          </button>
        )}
        {onExpandToFullPage && (
          <button
            style={iconButtonStyle}
            onClick={onExpandToFullPage}
            title="Open Full Command Center"
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(51,170,64,0.25)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
            }}
          >
            <ExternalLinkIcon size={14} color="#4DC95A" />
          </button>
        )}
        {onToggleMaximize && (
          <button
            style={iconButtonStyle}
            onClick={onToggleMaximize}
            title={isMaximized ? 'Minimize' : 'Maximize'}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.15)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
            }}
          >
            {isMaximized ? (
              <Minimize2Icon size={14} color="#C8D6DF" />
            ) : (
              <Maximize2Icon size={14} color="#C8D6DF" />
            )}
          </button>
        )}
        {onClose && (
          <button
            style={iconButtonStyle}
            onClick={onClose}
            title="Close"
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,86,86,0.2)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
            }}
          >
            <XIcon size={14} color="#C8D6DF" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Header;
